import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Stack,
  Divider,
  Slider,
  Grid,
  Card,
  CardContent,
  IconButton,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  useMediaQuery,
  TextField,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../contexts/AuthContext';
import { logOut } from '../../services/authService';
import { toast } from 'react-toastify';
import IconGrid from './components/IconGrid';
import { fetchWithAuth } from '../../api/backendApi';
import { LAUNCHER_GRID, clampGridColumns, clampGridRows } from '../../utils/launcherGrid';
import { fitDeviceFrame, matchGridPreset, estimateVisibleRows } from '../../utils/launcherPreview';
import { readLayoutFromStorage, persistLauncherBackgroundUrl, resolveLauncherBackgroundUrlAsync, writeLayoutToStorage } from '../../services/homeLauncher/launcherPersistenceService';
import { save as legacySave } from '../../services/homeLauncher/adapters/legacyLauncherAdapter';
import { reconcileHomePreferences, revisionOf } from '../../services/homeLauncher/homeLayoutSync';
import HomePersistenceNotice from './components/HomePersistenceNotice';
import { resolveAndLoad } from '../../services/workspaceRuntime/workspaceRuntimeService';
import { compressLauncherBackground, layoutSafeBackgroundUrl } from '../../utils/launcherBackgroundImage';

const GRID_PRESETS = {
  compact: { columns: 4, rows: 6, iconSize: 'small', gap: 1, label: 'Compact (4x6)' },
  phone: { columns: 4, rows: 6, iconSize: 'medium', gap: 2, label: 'Phone (4x6)' },
  tablet: { columns: 5, rows: 6, iconSize: 'medium', gap: 2, label: 'Tablet (5x6)' },
  desktop: { columns: 6, rows: 5, iconSize: 'large', gap: 3, label: 'Desktop (6x5)' },
  custom: { columns: 4, rows: 6, iconSize: 'medium', gap: 2, label: 'Custom' },
};

const PREVIEW_DEVICES = {
  phone: { label: 'Phone', width: 390, height: 780 },
  tablet: { label: 'Tablet', width: 820, height: 1180 },
  desktop: { label: 'Desktop', width: 1280, height: 720 },
};

const mergeDeviceResolutions = (stored = {}) => ({
  phone: { ...PREVIEW_DEVICES.phone, ...(stored.phone || {}) },
  tablet: { ...PREVIEW_DEVICES.tablet, ...(stored.tablet || {}) },
  desktop: { ...PREVIEW_DEVICES.desktop, ...(stored.desktop || {}) },
});

export default function LauncherSettingsPage() {
  const { currentUser, role, tenantId } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [themePref, setThemePref] = useState('system');
  const [preset, setPreset] = useState('phone');
  const [columns, setColumns] = useState(LAUNCHER_GRID.DEFAULT_COLUMNS);
  const [rows, setRows] = useState(LAUNCHER_GRID.DEFAULT_ROWS);
  const [iconSize, setIconSize] = useState('medium');
  const [gap, setGap] = useState(2);
  const [showGrid, setShowGrid] = useState(false);
  const [backgroundImageUrl, setBackgroundImageUrl] = useState('');
  const [bgUploading, setBgUploading] = useState(false);
  const [bgError, setBgError] = useState('');
  const [previewDevice, setPreviewDevice] = useState('phone');
  const [previewWidth, setPreviewWidth] = useState(String(PREVIEW_DEVICES.phone.width));
  const [previewHeight, setPreviewHeight] = useState(String(PREVIEW_DEVICES.phone.height));
  const [deviceResolutions, setDeviceResolutions] = useState(() => mergeDeviceResolutions());
  const [viewportSize, setViewportSize] = useState({ width: 0, height: 0 });
  const [stageSize, setStageSize] = useState({ width: 280, height: 360 });

  const bgInputRef = useRef(null);
  const loadGen = useRef(0);
  const baseRevisionRef = useRef(0);
  const [syncPhase, setSyncPhase] = useState('idle');
  const [syncServer, setSyncServer] = useState(null);
  const stageRef = useRef(null);
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');

  useEffect(() => {
    const gen = ++loadGen.current;
    const loadPreferences = async () => {
      if (!currentUser?.uid) return;
      setLoading(true);
      try {
        const cached = readLayoutFromStorage(currentUser.uid, tenantId) || {};
        let server = { stored: false, revision: 0, icons: [] };
        try {
          server = (await fetchWithAuth(`/api/v2/users/me/prefs/layout?tenantId=${encodeURIComponent(tenantId || '')}`, 'GET', null, { suppressErrorLog: true }))
            ?.data || server;
        } catch {
          server = null;
        }
        if (gen !== loadGen.current) return;
        const decision = server == null
          ? { action: cached.theme || cached.gridColumns ? 'keep-local' : 'use-server', layout: cached, serverLayout: null, localLayout: cached }
          : reconcileHomePreferences({
            server,
            local: cached.theme || cached.gridColumns || cached.icons ? cached : null,
            pending: Boolean(cached.pendingSave),
          });
        setSyncServer(decision.serverLayout || null);
        let data = decision.action === 'use-server' && server?.stored ? server : (decision.layout || cached);
        if (decision.action === 'conflict') {
          setSyncPhase('conflict');
        } else {
          if (decision.action === 'use-server') baseRevisionRef.current = revisionOf(server) ?? 0;
          setSyncPhase('idle');
        }

        try {
          const wsResult = await resolveAndLoad({ role });
          if (wsResult?.usedWorkspace && !cached.launcherSettingsSaved && wsResult.layoutData) {
            data = { ...wsResult.layoutData, ...data };
          }
        } catch {
          // workspace is optional on this page
        }

        const nextColumns = clampGridColumns(data.gridColumns || LAUNCHER_GRID.DEFAULT_COLUMNS);
        const nextRows = clampGridRows(data.gridRows || LAUNCHER_GRID.DEFAULT_ROWS);
        const nextIconSize = data.iconSize || 'medium';
        const nextGap = data.gap || 2;

        setThemePref(data.theme || 'system');
        setColumns(nextColumns);
        setRows(nextRows);
        setIconSize(nextIconSize);
        setGap(nextGap);
        setShowGrid(data.showGrid || false);
        const bg = await resolveLauncherBackgroundUrlAsync(
          currentUser.uid,
          data.backgroundImageUrl || '',
        );
        setBackgroundImageUrl(bg);
        setPreset(matchGridPreset(GRID_PRESETS, {
          columns: nextColumns,
          rows: nextRows,
          iconSize: nextIconSize,
          gap: nextGap,
        }));

        const mergedDevices = mergeDeviceResolutions(data.previewDevices);
        setDeviceResolutions(mergedDevices);

        const initialDevice = data.previewDevice || 'phone';
        setPreviewDevice(initialDevice);
        const activeCfg = mergedDevices[initialDevice] || mergedDevices.phone;
        setPreviewWidth(String(activeCfg.width));
        setPreviewHeight(String(activeCfg.height));
      } catch (error) {
        console.error('Error loading preferences:', error);
        toast.error('Failed to load settings');
      } finally {
        if (gen === loadGen.current) setLoading(false);
      }
    };
    loadPreferences();
  }, [currentUser?.uid, tenantId, role]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateSize = () => {
      setViewportSize({
        width: Math.round(window.innerWidth || 0),
        height: Math.round(window.innerHeight || 0),
      });
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (!cr) return;
      setStageSize({
        width: Math.max(1, Math.round(cr.width)),
        height: Math.max(1, Math.round(cr.height)),
      });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading]);

  const handlePresetChange = (newPreset) => {
    setPreset(newPreset);
    if (newPreset !== 'custom') {
      const config = GRID_PRESETS[newPreset];
      setColumns(config.columns);
      setRows(config.rows);
      setIconSize(config.iconSize);
      setGap(config.gap);
    }
  };

  const persistBackground = async (url) => {
    if (!currentUser?.uid) return;
    await persistLauncherBackgroundUrl(currentUser.uid, url || '');
    const existing = readLayoutFromStorage(currentUser.uid, tenantId) || {};
    const saved = await legacySave(currentUser.uid, {
      ...existing,
      backgroundImageUrl: layoutSafeBackgroundUrl(url),
      baseRevision: baseRevisionRef.current,
      revision: baseRevisionRef.current,
    }, { tenantId, immediate: true });
    if (saved?.revision != null) baseRevisionRef.current = saved.revision;
  };

  const handleBackgroundUpload = async (file) => {
    if (!file || !currentUser?.uid) return;
    setBgUploading(true);
    setBgError('');
    try {
      const url = await compressLauncherBackground(file);
      setBackgroundImageUrl(url);
      await persistBackground(url);
      toast.success('Background saved');
    } catch (err) {
      const message = err?.message || 'Could not save background';
      setBgError(message);
      toast.error(message);
    } finally {
      setBgUploading(false);
    }
  };

  const handleBackgroundRemove = async () => {
    setBackgroundImageUrl('');
    setBgError('');
    await persistBackground('');
  };

  const handleSave = async () => {
    if (!currentUser?.uid) return;
    try {
      const updates = {
        theme: themePref,
        gridColumns: clampGridColumns(columns),
        gridRows: clampGridRows(rows),
        iconSize,
        gap,
        preset,
        showGrid,
        backgroundImageUrl: layoutSafeBackgroundUrl(backgroundImageUrl),
        previewDevice,
        previewDevices: deviceResolutions,
        launcherSettingsSaved: true,
      };
      const existing = readLayoutFromStorage(currentUser.uid, tenantId) || {};
      await persistLauncherBackgroundUrl(currentUser.uid, backgroundImageUrl || '');
      setSyncPhase('pending');
      const saved = await legacySave(currentUser.uid, {
        ...existing,
        ...updates,
        baseRevision: baseRevisionRef.current,
        revision: baseRevisionRef.current,
      }, { tenantId, immediate: true });
      if (saved?.revision != null) baseRevisionRef.current = saved.revision;
      writeLayoutToStorage(currentUser.uid, {
        ...existing,
        ...updates,
        revision: saved?.revision ?? baseRevisionRef.current,
        pendingSave: false,
      }, tenantId);
      setSyncPhase('saved');
      toast.success('Settings saved successfully');
      navigate('/home');
    } catch (error) {
      console.error('Error saving settings:', error);
      setSyncPhase(String(error?.phase || '') === 'conflict' ? 'conflict' : 'error');
      toast.error(error?.message || 'Failed to save settings');
    }
  };

  const handleLogout = async () => {
    try {
      await logOut();
    } catch (e) {
      // toast handled by service
    }
  };

  const applyPreviewSize = (width, height) => {
    setPreviewWidth(String(width));
    setPreviewHeight(String(height));
    setDeviceResolutions((prev) => {
      const current = prev[previewDevice] || PREVIEW_DEVICES[previewDevice];
      return {
        ...prev,
        [previewDevice]: {
          ...current,
          width,
          height,
        },
      };
    });
  };

  const totalSlots = columns * rows;
  const isPreviewDark = themePref === 'dark' || (themePref === 'system' && prefersDark);
  const previewConfig = PREVIEW_DEVICES[previewDevice] || PREVIEW_DEVICES.phone;
  const widthPx = Number(previewWidth) || 0;
  const heightPx = Number(previewHeight) || 0;
  const deviceFrame = useMemo(
    () => fitDeviceFrame(stageSize.width, stageSize.height, widthPx, heightPx, 12),
    [stageSize.width, stageSize.height, widthPx, heightPx],
  );
  const { visibleRows: approxVisibleRows, willOverflow } = estimateVisibleRows({
    viewportHeight: heightPx || previewConfig.height,
    rows,
    iconSize,
    gap,
  });
  const visibleRowsLabel = approxVisibleRows.toFixed(1);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: '100vh',
      bgcolor: 'background.default',
      boxSizing: 'border-box',
      pl: 'var(--sb-workspace-rail-width, 0px)',
      pt: 'calc(var(--sb-workspace-chrome-top, 0px) + 24px)',
      pb: 3,
      overflowY: 'auto',
    }}>
      <Box sx={{ px: { xs: 2, md: 4 } }}>
        <HomePersistenceNotice
          phase={syncPhase}
          onRetry={handleSave}
          onUseServer={() => {
            if (!syncServer) return;
            baseRevisionRef.current = revisionOf(syncServer) ?? 0;
            setThemePref(syncServer.theme || 'system');
            setColumns(clampGridColumns(syncServer.gridColumns || LAUNCHER_GRID.DEFAULT_COLUMNS));
            setRows(clampGridRows(syncServer.gridRows || LAUNCHER_GRID.DEFAULT_ROWS));
            setIconSize(syncServer.iconSize || 'medium');
            setGap(syncServer.gap || 2);
            setShowGrid(Boolean(syncServer.showGrid));
            setSyncPhase('idle');
          }}
          onKeepLocal={handleSave}
        />
        <Box
          sx={{
            mb: 3,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.default',
            pt: 0.5,
            pb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/home')} size="large">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h4" component="h1">
                Launcher Settings
              </Typography>
            </Box>

            <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button onClick={() => navigate('/home')} variant="outlined">
                Cancel
              </Button>
              <Button onClick={handleSave} variant="contained">
                Save Changes
              </Button>
            </Stack>
          </Box>
        </Box>

        <Stack spacing={3}>
          <Grid container spacing={3} alignItems="stretch">
            {/* Theme Settings */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Appearance
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel id="theme-label">Theme</InputLabel>
                    <Select
                      labelId="theme-label"
                      value={themePref}
                      label="Theme"
                      onChange={(e) => setThemePref(e.target.value)}
                    >
                      <MenuItem value="system">System</MenuItem>
                      <MenuItem value="light">Light</MenuItem>
                      <MenuItem value="dark">Dark</MenuItem>
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography gutterBottom>
                      Background image
                    </Typography>
                    {backgroundImageUrl ? (
                      <Box
                        component="img"
                        src={backgroundImageUrl}
                        alt="Launcher background preview"
                        sx={{
                          width: '100%',
                          height: 88,
                          objectFit: 'cover',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          mb: 1,
                        }}
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Optional wallpaper behind the home grid. Same control as Home Designer.
                      </Typography>
                    )}
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Tooltip title={backgroundImageUrl ? 'Change background image' : 'Set background image'}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={bgUploading ? <CircularProgress size={14} /> : <WallpaperIcon fontSize="small" />}
                            disabled={bgUploading}
                            onClick={() => { setBgError(''); bgInputRef.current?.click(); }}
                          >
                            {backgroundImageUrl ? 'Change' : 'Background'}
                          </Button>
                        </span>
                      </Tooltip>
                      {backgroundImageUrl && (
                        <Tooltip title="Remove background image">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={handleBackgroundRemove}
                            disabled={bgUploading}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                    {bgError && (
                      <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                        {bgError}
                      </Typography>
                    )}
                    <input
                      ref={bgInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      style={{ display: 'none' }}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        e.target.value = '';
                        if (file) handleBackgroundUpload(file);
                      }}
                    />
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Grid Layout Settings */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Grid Layout
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel id="preset-label">Layout Preset</InputLabel>
                    <Select
                      labelId="preset-label"
                      value={preset}
                      label="Layout Preset"
                      onChange={(e) => handlePresetChange(e.target.value)}
                    >
                      {Object.entries(GRID_PRESETS).map(([key, config]) => (
                        <MenuItem key={key} value={key}>
                          {config.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography gutterBottom>
                      Columns: {columns}
                    </Typography>
                    <Slider
                      value={columns}
                      onChange={(e, val) => {
                        setColumns(val);
                        setPreset('custom');
                      }}
                      min={LAUNCHER_GRID.MIN_COLUMNS}
                      max={LAUNCHER_GRID.MAX_COLUMNS}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  <Box>
                    <Typography gutterBottom>
                      Rows: {rows}
                    </Typography>
                    <Slider
                      value={rows}
                      onChange={(e, val) => {
                        setRows(val);
                        setPreset('custom');
                      }}
                      min={LAUNCHER_GRID.MIN_ROWS}
                      max={LAUNCHER_GRID.MAX_ROWS}
                      step={1}
                      marks
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  <Box>
                    <Typography gutterBottom sx={{ mb: 1 }}>
                      Total Slots: {totalSlots}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Maximum number of buttons that can be placed on the grid
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Grid>

            {/* Icon Size Settings */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Typography variant="h6" gutterBottom>
                  Icon Appearance
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={3}>
                  <FormControl fullWidth>
                    <InputLabel id="icon-size-label">Icon Size</InputLabel>
                    <Select
                      labelId="icon-size-label"
                      value={iconSize}
                      label="Icon Size"
                      onChange={(e) => {
                        setIconSize(e.target.value);
                        setPreset('custom');
                      }}
                    >
                      <MenuItem value="small">Small (60px)</MenuItem>
                      <MenuItem value="medium">Medium (80px)</MenuItem>
                      <MenuItem value="large">Large (100px)</MenuItem>
                      <MenuItem value="xlarge">Extra Large (120px)</MenuItem>
                    </Select>
                  </FormControl>

                  <Box>
                    <Typography gutterBottom>
                      Spacing: {gap === 1 ? 'Compact' : gap === 2 ? 'Normal' : 'Spacious'}
                    </Typography>
                    <Slider
                      value={gap}
                      onChange={(e, val) => {
                        setGap(val);
                        setPreset('custom');
                      }}
                      min={1}
                      max={3}
                      step={1}
                      marks={[
                        { value: 1, label: 'Compact' },
                        { value: 2, label: 'Normal' },
                        { value: 3, label: 'Spacious' },
                      ]}
                      valueLabelDisplay="auto"
                    />
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={showGrid}
                        onChange={(e) => {
                          setShowGrid(e.target.checked);
                          setPreset('custom');
                        }}
                      />
                    }
                    label="Show Grid Lines"
                  />
                </Stack>
              </Paper>
            </Grid>

            {/* Preview Card */}
            <Grid item xs={12} sm={6} md={3} lg={3}>
              <Card sx={{ bgcolor: 'action.hover', height: '100%' }}>
                <CardContent sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 520 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" gutterBottom sx={{ mb: 0 }}>
                      Preview
                    </Typography>
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      value={previewDevice}
                      onChange={(event, value) => {
                        if (!value) return;
                        setPreviewDevice(value);
                        const cfg =
                          (deviceResolutions && deviceResolutions[value]) ||
                          PREVIEW_DEVICES[value] ||
                          PREVIEW_DEVICES.phone;
                        setPreviewWidth(String(cfg.width));
                        setPreviewHeight(String(cfg.height));
                      }}
                    >
                      <ToggleButton value="phone">Phone</ToggleButton>
                      <ToggleButton value="tablet">Tablet</ToggleButton>
                      <ToggleButton value="desktop">Desktop</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>

                  <Box
                    ref={stageRef}
                    sx={{
                      flexGrow: 1,
                      minHeight: 320,
                      maxHeight: 440,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mt: 1,
                    }}
                  >
                    <Box
                      sx={{
                        width: deviceFrame.width,
                        height: deviceFrame.height,
                        borderRadius: previewDevice === 'desktop' ? 1 : 3,
                        border: '1px solid',
                        borderColor: 'divider',
                        bgcolor: isPreviewDark ? '#0b0b0d' : '#f5f7fb',
                        backgroundImage: backgroundImageUrl ? `url(${backgroundImageUrl})` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        boxShadow: 1,
                        p: 1,
                        boxSizing: 'border-box',
                        position: 'relative',
                        overflow: 'hidden',
                      }}
                    >
                      <IconGrid
                        slots={[]}
                        editMode={false}
                        dark={isPreviewDark}
                        columns={columns}
                        gridMax={totalSlots}
                        iconSize={iconSize}
                        gap={gap}
                        showGrid={showGrid}
                        previewMode
                      />
                      {willOverflow && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            bottom: 0,
                            height: 24,
                            backgroundImage: `linear-gradient(to top, ${isPreviewDark ? '#0b0b0d' : '#f5f7fb'}, transparent)`,
                            pointerEvents: 'none',
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Stack direction="row" spacing={1.5} sx={{ mt: 1 }}>
                    <TextField
                      label="Width (px)"
                      size="small"
                      type="number"
                      value={previewWidth}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPreviewWidth(val);
                        const numeric = parseInt(val, 10);
                        const width = Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
                        setDeviceResolutions((prev) => {
                          const current = prev[previewDevice] || PREVIEW_DEVICES[previewDevice];
                          return {
                            ...prev,
                            [previewDevice]: {
                              ...current,
                              width,
                            },
                          };
                        });
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
                      fullWidth
                    />
                    <TextField
                      label="Height (px)"
                      size="small"
                      type="number"
                      value={previewHeight}
                      onChange={(e) => {
                        const val = e.target.value;
                        setPreviewHeight(val);
                        const numeric = parseInt(val, 10);
                        const height = Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
                        setDeviceResolutions((prev) => {
                          const current = prev[previewDevice] || PREVIEW_DEVICES[previewDevice];
                          return {
                            ...prev,
                            [previewDevice]: {
                              ...current,
                              height,
                            },
                          };
                        });
                      }}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 1 }}
                      fullWidth
                    />
                  </Stack>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    {previewConfig.label} viewport • {widthPx || '—'}×{heightPx || '—'} px • {columns}×{rows} grid, {totalSlots} slots
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      This browser: {viewportSize.width || '—'} × {viewportSize.height || '—'} px
                    </Typography>
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => {
                        if (!viewportSize.width || !viewportSize.height) return;
                        applyPreviewSize(viewportSize.width, viewportSize.height);
                      }}
                    >
                      Use current
                    </Button>
                  </Box>
                  <Typography
                    variant="caption"
                    color={willOverflow ? 'warning.main' : 'text.secondary'}
                    sx={{ display: 'block', mt: 0.5 }}
                  >
                    Approx. {visibleRowsLabel} of {rows} rows visible at {heightPx || '—'} px height
                    {willOverflow ? ' — remaining rows will require scrolling.' : ' (no vertical overflow).'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Stack direction="row" spacing={2} justifyContent="flex-start">
            <Button onClick={handleLogout} color="error" variant="outlined">
              Logout
            </Button>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
}
